/* eslint-disable import/no-extraneous-dependencies */
import { createRoot } from 'react-dom/client';

import '@april9au/stack9-ui/dist/index.css';
import './App.css';

import 'codemirror/addon/display/placeholder';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/addon/search/match-highlighter';
import 'codemirror/mode/handlebars/handlebars';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/sql/sql';

import * as emailModule from '@april9au/stack9-email-ui';
import { AppProvider, AuthProvider } from '@april9au/stack9-react';
import {
  Alert,
  App,
  AppContextProvider,
  Avatar,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Calendar,
  Card,
  Collapse,
  CollapsePanel,
  Container,
  DescriptionItem,
  Descriptions,
  Empty,
  Image,
  LayoutContent,
  LayoutFooter,
  LayoutHeader,
  LayoutSider,
  Menu,
  Modal,
  Pagination,
  Paragraph,
  Progress,
  RadioGroup,
  Rate,
  S9Button,
  S9Checkbox,
  S9Col,
  S9CustomEntityFormPage,
  S9DateField,
  S9EntityActions,
  S9EntityWorkflowActions,
  S9EntityWorkflowHistory,
  S9Fieldset,
  S9FileField,
  S9Form,
  S9Layout,
  S9Grid,
  S9MonacoEditorField,
  S9MultiDropDown,
  S9NumericField,
  S9OptionSet,
  S9PageContent,
  S9PageHeader,
  S9PageTitle,
  S9Row,
  S9SingleDropDown,
  S9Table,
  S9Tabs,
  S9TextField,
  S9RichTextEditor,
  SaveFilterButton,
  Select,
  Skeleton,
  Spin,
  Statistic,
  Step,
  Steps,
  Switch,
  TabPane,
  TimePicker,
  Timeline,
  TimelineItem,
  UIProvider,
  axiosProvider,
  reportWebVitals,
  stack9Config,
} from '@april9au/stack9-ui';
import { ConfigProvider } from 'antd';

import { ActionBarComponent } from './components/ActionBarComponent';
import {
  ApplicationResponseAction,
  BaseOptionSetAction,
  DependencyAction,
  FileView,
  IndustryDrawerAction,
  OccupationDrawerAction,
  OptionSetAction,
  PriceRuleDrawerAction,
  PromoCodeDrawerAction,
  SecondaryIndustryDrawerAction,
  SurveyQuestionDrawerAction,
} from './components/ListViewCustomComponents';
import { AccountQueryDropdown } from './components/QueryDropdowns';
import Account from './pages/Account/Index';
import Application from './pages/Application/ApplicationDetail';
import BaseRequirement from './pages/BaseRequirement/BaseRequirementDetail';
import CountrySetup from './pages/CountrySetup/CountrySetupDetail';
import CountryTemplate from './pages/CountryTemplate/CountryTemplateDetail';
import Organisation from './pages/Organisation/Index';
import Partner from './pages/Partner/PartnerDetail';
import Requirement from './pages/Requirement/RequirementDetail';

const components = [
  S9Button,
  Alert,
  Avatar,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Calendar,
  Card,
  S9Col,
  Collapse,
  CollapsePanel,
  Container,
  DescriptionItem,
  Descriptions,
  Empty,
  Image,
  S9Layout,
  LayoutContent,
  LayoutFooter,
  LayoutHeader,
  LayoutSider,
  Menu,
  Modal,
  Pagination,
  Paragraph,
  Progress,
  RadioGroup,
  Rate,
  S9Row,
  Select,
  Skeleton,
  Spin,
  Statistic,
  Step,
  Steps,
  Switch,
  S9Table,
  TabPane,
  S9Tabs,
  Timeline,
  TimelineItem,
  TimePicker,
  S9PageTitle,
  S9PageHeader,
  S9PageContent,
  S9EntityWorkflowActions,
  S9EntityWorkflowHistory,
  S9EntityActions,
  S9FileField,
  S9TextField,
  S9RichTextEditor,
  S9Form,
  S9Fieldset,
  S9OptionSet,
  S9SingleDropDown,
  S9MultiDropDown,
  S9DateField,
  S9NumericField,
  S9Checkbox,
  S9Grid,
  S9MonacoEditorField,
  SaveFilterButton,
  S9CustomEntityFormPage,
  ApplicationResponseAction,
  BaseOptionSetAction,
  DependencyAction,
  FileView,
  IndustryDrawerAction,
  OccupationDrawerAction,
  OptionSetAction,
  PriceRuleDrawerAction,
  PromoCodeDrawerAction,
  SecondaryIndustryDrawerAction,
  SurveyQuestionDrawerAction,
  AccountQueryDropdown,
  ...emailModule.components,
];

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <AppProvider axiosFactory={axiosProvider} config={stack9Config}>
    <AuthProvider
      clientId="" // Required in V5, empty string for backoffice
      onError={err => {
        console.error(err);
      }}
    >
      <UIProvider components={components} extensions={{ ActionBarComponent }}>
        <AppContextProvider>
          <ConfigProvider>
            <App
              customRoutes={[
                {
                  route: '/organisation-detail/:id',
                  component: <Organisation />,
                },
                {
                  route: '/partner-detail/:id',
                  component: <Partner />,
                },
                {
                  route: '/application-detail/:id',
                  component: <Application />,
                },
                {
                  route: '/account-detail/:id',
                  component: <Account />,
                },
                {
                  route: '/base-requirement-detail/:id',
                  component: <BaseRequirement />,
                },
                {
                  route: '/requirement-detail/:id',
                  component: <Requirement />,
                },
                {
                  route: '/country-setup-detail/:id',
                  component: <CountrySetup />,
                },
                {
                  route: '/country-template-detail/:id',
                  component: <CountryTemplate />,
                },
                ...emailModule.customRoutes,
              ]}
            />
          </ConfigProvider>
        </AppContextProvider>
      </UIProvider>
    </AuthProvider>
  </AppProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
